//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import angular, { IScope } from 'angular';
import { PojoSet } from 'pojo-sets';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'unison';

import * as gatewayMediaApi from '@apis/gatewayMediaApi';
import {
  InvisibleButton,
  PrimaryButton,
  TertiaryButton,
} from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { ManageLabelsDialog } from '@content-drawer/filters/labels/ManageLabelsDialog';
import { useAllLabelsQuery } from '@content-drawer/filters/labels/hooks/useAllLabelsQuery';
import makeAngularReactControllerWithScope, {
  applyScope,
} from '@react-bridge/angular-react-widget-with-scope';
import { createHasFeatureSelector } from '@redux/selectors/createHasFeatureSelector';

import { LabelsModalContent } from 'app/components/content/content-library-labels-modal/LabelsModalContent';
import { GRID_HEIGHT } from 'app/components/content/content-library-labels-modal/constants';

interface Props {
  onCloseClick: (args: { labels?: string[]; shouldRefresh?: boolean }) => void;
  initiallySelectedLabels: string[];
}

// TODO: PD-269679 - Remove this component when Collections is migrated to the new Filters Sidebar
export const LabelsModal = ({
  initiallySelectedLabels,
  onCloseClick,
}: Props) => {
  const [selectedLabels, setSelectedLabels] = useState<PojoSet<string>>({});

  const { data: response, isFetching } = useAllLabelsQuery();
  const allLabels = response?.data.indexObjects ?? [];

  const [hasChanged, setHasChanged] = useState(false);

  const isOpen = useSelector((state) => state.viewAllLabels.isModalOpen);
  const isLabelManagementEnabled = useSelector(
    createHasFeatureSelector(FeatureNames.LabelManagement),
  );

  useEffect(() => {
    const filteredSelectedLabels = initiallySelectedLabels.filter((label) =>
      (response?.data.indexObjects ?? []).some(
        (labelObject) =>
          labelObject.userTag.toLowerCase() === label.toLowerCase(),
      ),
    );
    setSelectedLabels(PojoSet.from(filteredSelectedLabels));
  }, [initiallySelectedLabels, response?.data.indexObjects]);

  const onModalClose = (returnedLabels?: string[]) => {
    setSelectedLabels({});
    onCloseClick({ labels: returnedLabels, shouldRefresh: hasChanged });
    setHasChanged(false);
  };

  const handleCheckboxClick = (isChecked: boolean, labelValue: string) => {
    setHasChanged(true);

    if (isChecked) {
      setSelectedLabels(PojoSet.add(selectedLabels, labelValue));
    } else {
      setSelectedLabels(PojoSet.remove(selectedLabels, labelValue));
    }
  };

  return (
    <StyledModal isOpen={isOpen}>
      <StyledModalHeader>
        <ModalHeaderContainer>
          <ModalHeaderText>All Labels</ModalHeaderText>
          {isLabelManagementEnabled && (
            <ManageLabelsDialog
              onToggleModal={() => setHasChanged(true)}
              shouldHideOverlay={true}
            />
          )}
        </ModalHeaderContainer>
        <InvisibleButton onClick={() => onModalClose()} type='button'>
          <ModalCloseButton />
        </InvisibleButton>
      </StyledModalHeader>
      <ModalBody>
        {isFetching ? (
          <SpinnerContainer>
            <CenteredSpinner />
          </SpinnerContainer>
        ) : (
          <LabelsModalContent
            allLabels={allLabels.map((labelObject) => ({
              value: labelObject.userTag,
              label: labelObject.userTag,
              count: labelObject.itemsWithTag,
            }))}
            handleCheckboxClick={handleCheckboxClick}
            handleSearch={(_, searchQuery) =>
              gatewayMediaApi.searchLabelsDeprecated(searchQuery)
            }
            selectedLabels={selectedLabels}
          />
        )}
      </ModalBody>
      <ModalFooter>
        <TertiaryButton onClick={() => onModalClose()}>Cancel</TertiaryButton>
        <Spacer pxWidth={5} />
        <PrimaryButton
          onClick={() => onModalClose(PojoSet.toArray(selectedLabels))}
        >
          Apply
        </PrimaryButton>
      </ModalFooter>
    </StyledModal>
  );
};

const SpinnerContainer = styled.div`
  min-height: ${GRID_HEIGHT}px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledModal = styled(Modal)`
  width: ${({ theme }) => theme.wideMeasure};
`;

const StyledModalHeader = styled(ModalHeader)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalHeaderText = styled.div`
  margin-right: ${({ theme }) => theme.spacings[1]};
`;

const ModalHeaderContainer = styled.div`
  display: flex;
`;

const ModalCloseButton = styled(Icon).attrs({
  name: 'close',
})`
  cursor: pointer;
`;

const Spacer = styled.div<{ pxWidth: number }>(
  ({ pxWidth }) => `
  width: ${pxWidth}px;
  display: inline-block;
  flex-shrink: 0;
`,
);

angular.module('curalate').component('contentLibraryLabelsModal', {
  controller: makeAngularReactControllerWithScope(
    (scope: { $ctrl: Props } & IScope) => {
      return (
        <LabelsModal
          initiallySelectedLabels={scope.$ctrl.initiallySelectedLabels}
          onCloseClick={applyScope(scope, scope.$ctrl.onCloseClick)}
        />
      );
    },
  ),
  bindings: {
    isOpen: '<',
    onCloseClick: '&',
    initiallySelectedLabels: '<',
  },
});
